import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { api, formatEuro, formatHour, formatEndHour, STUDIO_LABELS } from "@/lib/api";
import { CheckCircle2, Loader2, AlertTriangle, CalendarDays } from "lucide-react";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [booking, setBooking] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 8) {
        setState("pending");
        return;
      }
      attemptsRef.current += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setBooking(res.data.booking);
          setState("paid");
          return;
        }
        if (res.data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-16 sm:px-6">
      {state === "checking" && (
        <Card className="border-border bg-card" data-testid="payment-checking-card">
          <CardContent className="flex flex-col items-center p-10 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <h1 className="mt-5 font-heading text-xl font-semibold">Vérification du paiement…</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Merci de patienter quelques secondes, nous confirmons votre paiement auprès de Stripe.
            </p>
          </CardContent>
        </Card>
      )}

      {state === "paid" && booking && (
        <Card className="border-primary/40 bg-card" data-testid="payment-success-card">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-10 w-10 text-primary" />
              <div>
                <h1 className="font-heading text-2xl font-semibold">Paiement confirmé !</h1>
                <p className="text-sm text-muted-foreground">
                  Votre créneau est réservé et payé — c'est confirmé !
                </p>
              </div>
            </div>
            <Separator className="my-6" />
            <div className="flex items-start gap-2.5 rounded-lg border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] px-4 py-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <p className="text-sm text-foreground/90" data-testid="payment-email-notice">
                Un email de confirmation vous a été envoyé. Notre équipe a également été prévenue de
                votre venue. Rappel : annulation gratuite jusqu'à 24h avant la session. En cas
                d'empêchement, prévenez-nous au moins 2 heures avant la session, sinon le paiement
                sera encaissé sans aucun remboursement dû.
              </p>
            </div>
            <Separator className="my-6" />
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Référence</span>
                <span className="font-mono-nums text-xs">{booking.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Espace</span>
                <span className="font-medium">{STUDIO_LABELS[booking.studio_type]}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Service</span>
                <span className="font-medium">{booking.service_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date</span>
                <span className="font-medium capitalize">
                  {format(parseISO(booking.date), "EEEE d MMMM yyyy", { locale: fr })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Horaire</span>
                <span className="font-mono-nums font-medium">
                  {formatHour(booking.start_hour)} – {formatEndHour(booking.end_hour)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Montant payé</span>
                <span className="font-mono-nums text-lg font-semibold text-primary" data-testid="payment-success-amount">
                  {formatEuro(booking.amount)}
                </span>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="success-back-planning">
                <Link to="/planning">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Voir le planning
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/">Retour à l'accueil</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {state === "pending" && (
        <Card className="border-border bg-card" data-testid="payment-pending-card">
          <CardContent className="flex flex-col items-center p-10 text-center">
            <AlertTriangle className="h-10 w-10 text-primary" />
            <h1 className="mt-5 font-heading text-xl font-semibold">Paiement en cours de traitement</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              La confirmation prend plus de temps que prévu. Votre réservation sera confirmée dès
              réception du paiement.
            </p>
            <Button className="mt-6" variant="outline" onClick={() => window.location.reload()}>
              Actualiser
            </Button>
          </CardContent>
        </Card>
      )}

      {state === "error" && (
        <Card className="border-destructive/40 bg-card" data-testid="payment-error-card">
          <CardContent className="flex flex-col items-center p-10 text-center">
            <AlertTriangle className="h-10 w-10 text-destructive" />
            <h1 className="mt-5 font-heading text-xl font-semibold">Paiement non confirmé</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, contactez-nous.
            </p>
            <div className="mt-6 flex gap-3">
              <Button asChild>
                <Link to="/reserver">Réessayer</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/">Accueil</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
